import { TextField, TextFieldProps } from '@mui/material';
import { useRef, useState } from 'react';
import { color } from '@/colors';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';

interface NodeTextFieldProps extends Omit<TextFieldProps, 'expandable'> {
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  rootSx?: Record<string, unknown>;
}

export const NodeTextField = ({
  sx,
  rootSx,
  slotProps,
  color: textColor,
  minHeight,
  maxHeight,
  ...props
}: NodeTextFieldProps) => {
  const inputRef = useRef<HTMLDivElement | null>(null);
  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const [isFocused, setIsFocused] = useState(false);

  useCaptureElementWheelScroll(isHovered || isFocused ? inputRef : { current: null });

  return (
    <FlexCol
      sx={{
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: minHeight ?? 0,
        maxHeight,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="nodrag"
    >
      <TextField
        {...props}
        onFocus={(e) => {
          setIsFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          props.onBlur?.(e);
        }}
        slotProps={{
          ...slotProps,
          input: {
            ...(slotProps?.input || {}),
            readOnly: props.readOnly,
            ref: inputRef,
            style: {
              fontSize: '16px',
              fontWeight: 400,
              color: textColor || color.White100,
            },
          },
        }}
        sx={{
          overflow: 'hidden',
          bgcolor: color.Black84,
          borderColor: color.White04_T,
          borderRadius: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            padding: '24px',
            flex: 1,
            alignItems: 'stretch',
            overflowY: 'auto',
            border: `1px solid ${color.White04_T}`,
            '& fieldset': {
              display: 'none',
            },
            '& textarea::placeholder': {
              color: color.White40_T,
            },
            ...rootSx,
            '& textarea': {
              resize: 'none',
              ...(rootSx?.['& textarea'] ?? {}),
            },
          },
          ...sx,
        }}
      />
    </FlexCol>
  );
};
